import { useLayoutEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Home, HeartHandshake, Wallet, Baby, ShieldCheck, CalendarClock, Scale,
  Building2, BookOpen, UserCheck, MessageCircle, ListChecks, FileCheck, RefreshCw,
  ChevronRight, Phone, ArrowUpRight,
} from "lucide-react";

import { SmoothScroll } from "@/components/SmoothScroll";
import { ScheduleProvider, useSchedule } from "@/components/ScheduleModal";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { SITE, businessEntity, orgRef } from "@/data/business";

const PHONE_HREF = "tel:+16234141766";
const PATH = "/life-insurance-chandler-az";

const INTRO =
  "Choosing life insurance is one of the most caring decisions you can make for the people who depend on you. As an independent life insurance broker, John Campbell helps individuals and families in Chandler — and across Arizona — understand their options clearly and without pressure, so the decision always stays yours.";

const LOCAL_TRUST = [
  "John Campbell Insurance is an independent life insurance brokerage based in Chandler, Arizona. John is licensed in Arizona and works with individuals and families in Chandler and throughout the East Valley.",
  "Because the agency is independent, John isn't tied to a single insurance company. He compares coverage across more than 25 A-rated or better insurance carriers, explains your options in plain language, and stays with you as your family and priorities change. The relationship doesn't end once a policy is in place.",
  "Consultations are available by phone, by video, or in person by appointment. John Campbell Insurance does not operate a public walk-in office, so we'll meet in whichever way is most convenient for you.",
];

const EXPLORE = [
  { title: "Term life insurance", desc: "Protection for the years your family depends on your income most, often at a sensible cost.", link: "Learn about term life →", to: "/products/term-life-insurance" },
  { title: "Whole life insurance", desc: "Lifelong protection with level premiums and cash value that builds over time.", link: "Learn about whole life →", to: "/products/whole-life-insurance" },
  { title: "Mortgage protection", desc: "Helps make sure the people you love can stay in the home if the unexpected happens.", link: "Learn about mortgage protection →", to: "/products/mortgage-protection-insurance" },
  { title: "Retirement solutions", desc: "Strategies designed to help create dependable income for the years ahead.", link: "Learn about retirement solutions →", to: "/products/retirement-solutions" },
];

const NEEDS = [
  { icon: Wallet, title: "Income replacement", desc: "Help replace lost income so your family can maintain their way of life if you're no longer there to provide." },
  { icon: HeartHandshake, title: "Family responsibilities", desc: "Give the people you love time and stability to grieve and regroup without added financial pressure." },
  { icon: Home, title: "Mortgage & home", desc: "Coverage can help pay off or reduce the mortgage so loved ones aren't forced to sell or move." },
  { icon: Baby, title: "Debts & obligations", desc: "Help settle outstanding debts and everyday obligations rather than passing them on to your family." },
  { icon: ShieldCheck, title: "Final expenses", desc: "Ease the burden of funeral, burial, and other end-of-life costs with dedicated coverage." },
  { icon: CalendarClock, title: "Long-term plans", desc: "Certain policies can build value over time to support retirement, legacy, and lifelong goals." },
];

const WHY = [
  { icon: Scale, title: "Independent broker", desc: "John is not limited to a single insurance company, so recommendations can be built around your needs." },
  { icon: Building2, title: "25+ A-rated or better insurance carriers", desc: "Access to more than 25 A-rated or better insurance carriers means we compare options rather than settle." },
  { icon: BookOpen, title: "Education first", desc: "You'll understand your options before any recommendation, so every decision is truly informed." },
  { icon: UserCheck, title: "Relationship-first", desc: "A real advisor who knows your name and is here long after the paperwork is signed." },
];

const PROCESS = [
  { icon: MessageCircle, step: "01", title: "A simple conversation", desc: "We start by listening — your goals, your family, your budget, and the season of life you're in." },
  { icon: ListChecks, step: "02", title: "Options compared for you", desc: "John compares suitable coverage across 25+ A-rated or better insurance carriers, then explains them in plain language." },
  { icon: FileCheck, step: "03", title: "A clear recommendation", desc: "You receive a straightforward recommendation you can understand and trust — with no pressure to decide." },
  { icon: RefreshCw, step: "04", title: "Ongoing reviews", desc: "As life changes, we revisit your coverage together so it stays aligned with your goals over time." },
];

const FAQS = [
  {
    q: "Do you meet with clients in Chandler?",
    a: "Yes. John works with individuals and families throughout Chandler and the greater Phoenix area, as well as across Arizona and in select states. Consultations are available by phone, video, or in person by appointment — there is no walk-in office.",
  },
  {
    q: "How much does a consultation cost?",
    a: "Consultations are complimentary and carry no obligation. The goal is simply to help you understand your options clearly, whether or not you ever purchase a policy.",
  },
  {
    q: "What types of life insurance can you help with?",
    a: "As an independent broker, John can help you compare term, whole, universal, and indexed universal life insurance, along with mortgage protection and final expense coverage, across more than 25 A-rated or better insurance carriers.",
  },
  {
    q: "Why work with an independent broker instead of a single company?",
    a: "An independent broker isn't tied to one insurer. That means recommendations are built around your needs and budget — comparing options from many A-rated companies rather than a single product line.",
  },
  {
    q: "Do you serve areas outside Chandler?",
    a: "Yes. In addition to Chandler and communities throughout Arizona, John Campbell Insurance serves clients in select states. Availability varies by state, and additional states may be considered when appropriate.",
  },
  {
    q: "How do we get started?",
    a: "The easiest way is to schedule a no-cost consultation or request a quote. We'll begin with a simple conversation about your goals, then review your options together at your pace.",
  },
];

const SectionHeading = ({ kicker, children, className = "" }) => (
  <div className={className}>
    {kicker && <span className="mono text-[10px] uppercase tracking-[0.35em] text-[#d4af37] block mb-4">{kicker}</span>}
    <h2 className="serif font-light leading-[1.05] text-[#f4f0ea] text-3xl sm:text-4xl lg:text-5xl">{children}</h2>
  </div>
);

const Content = () => {
  const { openSchedule } = useSchedule();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true, force: true });
  }, []);

  return (
    <>
      {/* HERO */}
      <section data-testid="chandler-hero" className="relative min-h-[80vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src="/hero-bg.png" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#030303] via-[#030303]/80 to-[#030303]/45" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#030303]/85 to-transparent" />
        </div>
        <div className="relative w-full max-w-[1400px] mx-auto px-6 lg:px-10 pb-16 lg:pb-24 pt-32">
          <nav data-testid="breadcrumbs" aria-label="Breadcrumb" className="mb-8">
            <ol className="flex flex-wrap items-center gap-2 mono text-[10px] uppercase tracking-[0.25em] text-[#737373]">
              <li><Link to="/" className="hover:text-[#d4af37] transition-colors">Home</Link></li>
              <li><ChevronRight size={12} className="text-[#525252]" /></li>
              <li className="text-[#a3a3a3]">Life Insurance in Chandler, AZ</li>
            </ol>
          </nav>
          <span className="mono text-[10px] uppercase tracking-[0.3em] text-[#f0d57d] bg-black/50 backdrop-blur-md border border-white/10 rounded-full px-4 py-1.5">Chandler, Arizona</span>
          <h1 className="serif font-light leading-[1.02] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl xl:text-[4.2rem] mt-6 max-w-4xl">
            <motion.span
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="block"
            >
              Life Insurance in <span className="italic text-[#d4af37]">Chandler, Arizona</span>
            </motion.span>
          </h1>
          <Reveal delay={0.2}>
            <p className="text-[#c9c4bd] leading-relaxed tracking-wide text-lg mt-6 max-w-2xl">{INTRO}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="flex flex-col sm:flex-row gap-4 mt-10">
              <button onClick={() => openSchedule()} data-testid="chandler-hero-schedule" className="mono text-xs uppercase tracking-[0.2em] px-8 py-4 bg-[#d4af37] text-[#030303] hover:bg-[#e6c860] transition-colors duration-400 rounded-full">
                Schedule a Consultation
              </button>
              <a href={PHONE_HREF} data-testid="chandler-hero-call" className="mono text-xs uppercase tracking-[0.2em] px-8 py-4 border border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#030303] transition-colors duration-400 rounded-full flex items-center justify-center gap-2">
                <Phone size={14} /> Call John
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.4}>
            <p className="mono text-[10px] sm:text-[11px] uppercase tracking-[0.2em] text-[#a3a3a3] mt-7 flex flex-wrap items-center gap-x-3 gap-y-2">
              <span>Independent Broker</span><span className="text-[#d4af37]">•</span>
              <span>25+ A-Rated or Better Insurance Carriers</span><span className="text-[#d4af37]">•</span>
              <span>No-Cost Consultation</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* LOCAL TRUST */}
      <section data-testid="chandler-local-trust" className="relative bg-[#0a0a0a] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10">
          <Reveal className="mb-8">
            <SectionHeading kicker="Based in Chandler">A Chandler-based independent life insurance broker</SectionHeading>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-6 border-l-2 border-[#d4af37]/40 pl-8 text-[#c9c4bd] leading-relaxed tracking-wide text-lg">
              {LOCAL_TRUST.map((t) => <p key={t.slice(0, 24)}>{t}</p>)}
              <p>
                <Link to="/client-experiences" data-testid="chandler-client-experiences-link" className="text-[#d4af37] hover:text-[#f0d57d] link-underline">
                  See what clients have shared about working with John Campbell Insurance →
                </Link>
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* PROTECTION NEEDS */}
      <section data-testid="chandler-needs" className="relative bg-[#030303] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal className="mb-12 max-w-3xl">
            <SectionHeading kicker="What it can protect">The needs life insurance can help cover</SectionHeading>
            <p className="text-[#a3a3a3] leading-relaxed tracking-wide text-lg mt-6">
              Every family is different. For many people in Chandler, life insurance is about making sure the people they
              love are cared for — here are some of the most common needs it can help address.
            </p>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {NEEDS.map((n, i) => {
              const I = n.icon;
              return (
                <Reveal key={n.title} delay={i * 0.07}>
                  <div className="group bg-[#0a0a0a] border border-white/10 rounded-sm p-8 h-full hover:border-[#d4af37]/40 transition-colors duration-500">
                    <span className="inline-flex h-12 w-12 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-400">
                      <I size={20} className="text-[#d4af37]" />
                    </span>
                    <h3 className="serif text-2xl text-[#f4f0ea] mb-3">{n.title}</h3>
                    <p className="text-[#a3a3a3] leading-relaxed tracking-wide">{n.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* EXPLORE COVERAGE */}
      <section data-testid="chandler-explore" className="relative bg-[#0a0a0a] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal className="mb-12 max-w-3xl">
            <SectionHeading kicker="Explore your options">
              Find the coverage that <span className="italic text-[#d4af37]">fits your situation</span>
            </SectionHeading>
            <p className="text-[#a3a3a3] leading-relaxed tracking-wide text-lg mt-6">
              Every family's priorities are different. If you'd like to learn more before we talk, these guides explain the
              coverage people ask about most.
            </p>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {EXPLORE.map((c, i) => (
              <Reveal key={c.to} delay={i * 0.07}>
                <Link to={c.to} data-testid={`chandler-explore-${c.to.split("/").pop()}`} className="group flex flex-col bg-[#030303] border border-white/10 rounded-sm p-8 h-full hover:border-[#d4af37]/40 transition-colors duration-500">
                  <h3 className="serif text-2xl text-[#f4f0ea] mb-3">{c.title}</h3>
                  <p className="text-[#a3a3a3] leading-relaxed tracking-wide flex-1">{c.desc}</p>
                  <span className="mono text-[10px] uppercase tracking-[0.2em] text-[#d4af37] mt-6 group-hover:text-[#f0d57d] transition-colors">{c.link}</span>
                </Link>
              </Reveal>
            ))}
          </div>
          <p className="text-[#737373] leading-relaxed mt-10">Not sure where to start? That's exactly what a consultation is for.</p>
        </div>
      </section>

      {/* APPROACH */}
      <section data-testid="chandler-approach" className="relative bg-[#0a0a0a] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10">
          <Reveal className="mb-8">
            <SectionHeading kicker="Our approach">Education first, always</SectionHeading>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-6 border-l-2 border-[#d4af37]/40 pl-8 text-[#c9c4bd] leading-relaxed tracking-wide text-lg">
              <p>
                John's approach is simple: help you understand your options first, then build a personalized recommendation
                around them. No scripts, no pressure — just clear, honest guidance you can trust.
              </p>
              <p>
                Because John Campbell Insurance is independent, the focus is on what fits your family and budget, comparing
                coverage across more than 25 A-rated or better insurance carriers rather than defending a single product line. You can
                read more <Link to="/#about" className="text-[#d4af37] hover:text-[#f0d57d] link-underline">about our approach</Link>.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* WHY */}
      <section data-testid="chandler-why" className="relative bg-[#030303] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal className="mb-12 max-w-3xl">
            <SectionHeading kicker="Why work with John Campbell Insurance">
              An advisor who <span className="italic text-[#d4af37]">educates first</span>.
            </SectionHeading>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
            {WHY.map((t, i) => {
              const T = t.icon;
              return (
                <Reveal key={t.title} delay={(i % 4) * 0.06}>
                  <div className="bg-[#030303] p-8 h-full hover:bg-[#0a0a0a] transition-colors duration-400">
                    <T size={22} className="text-[#d4af37] mb-5" />
                    <h3 className="text-[#f4f0ea] text-lg mb-2 tracking-wide">{t.title}</h3>
                    <p className="text-[#737373] text-sm leading-relaxed">{t.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section data-testid="chandler-process" className="relative bg-[#0a0a0a] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal className="mb-12 max-w-3xl">
            <SectionHeading kicker="How it works">A simple, no-pressure process</SectionHeading>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PROCESS.map((p, i) => {
              const I = p.icon;
              return (
                <Reveal key={p.step} delay={i * 0.08}>
                  <div className="bg-[#030303] border border-white/10 rounded-sm p-8 h-full hover:border-[#d4af37]/40 transition-colors duration-500">
                    <div className="flex items-center justify-between mb-5">
                      <I size={22} className="text-[#d4af37]" />
                      <span className="mono text-sm text-[#d4af37]">{p.step}</span>
                    </div>
                    <h3 className="serif text-xl text-[#f4f0ea] mb-3">{p.title}</h3>
                    <p className="text-[#a3a3a3] text-sm leading-relaxed tracking-wide">{p.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICE AREA */}
      <section data-testid="chandler-service-area" className="relative bg-[#030303] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10">
          <Reveal>
            <SectionHeading kicker="Service area">Serving Chandler and beyond</SectionHeading>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[#a3a3a3] leading-relaxed tracking-wide text-lg mt-6">
              Along with Chandler and the East Valley, John Campbell Insurance serves individuals and families throughout
              Arizona and in select states. Availability varies by state, and additional states may be considered when
              appropriate.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section data-testid="chandler-faq" className="relative bg-[#0a0a0a] py-16 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-20">
          <Reveal>
            <SectionHeading kicker="Common questions">
              Questions people<br /><span className="italic text-[#d4af37]">often ask</span>.
            </SectionHeading>
            <p className="text-[#737373] leading-relaxed mt-6 max-w-sm">
              Still wondering about something? A no-cost consultation is the best way to get answers tailored to you.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} className="border-white/10">
                  <AccordionTrigger data-testid={`chandler-faq-${i}`} className="serif text-lg sm:text-xl text-[#f4f0ea] hover:no-underline hover:text-[#d4af37] py-6 [&>svg]:text-[#d4af37]">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[#a3a3a3] leading-relaxed tracking-wide text-base pb-6 pr-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="chandler-cta" className="relative bg-[#030303] py-20 lg:py-28 border-t border-white/10">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 text-center">
          <Reveal>
            <h2 className="serif font-light leading-[1.05] text-[#f4f0ea] text-4xl sm:text-5xl lg:text-6xl">
              Protect what <span className="italic text-[#d4af37]">matters most</span>.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-[#a3a3a3] leading-relaxed tracking-wide text-lg mt-6 max-w-2xl mx-auto">
              Whether you're exploring life insurance for the first time or reviewing an existing policy, John is here to help
              you understand your options and make an informed decision with confidence.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
              <button onClick={() => openSchedule()} data-testid="chandler-final-schedule" className="mono text-xs uppercase tracking-[0.2em] px-8 py-4 bg-[#d4af37] text-[#030303] hover:bg-[#e6c860] transition-colors duration-400 rounded-full">
                Schedule a Consultation
              </button>
              <Link to="/#quote" data-testid="chandler-final-quote" className="mono text-xs uppercase tracking-[0.2em] px-8 py-4 border border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#030303] transition-colors duration-400 rounded-full flex items-center justify-center gap-2">
                Get a Quote <ArrowUpRight size={15} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default function LifeInsuranceChandler() {
  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Life Insurance",
    name: "Life Insurance in Chandler, AZ",
    description: INTRO,
    areaServed: [
      { "@type": "City", name: "Chandler" },
      { "@type": "State", name: "Arizona" },
    ],
    provider: orgRef,
  };
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
      { "@type": "ListItem", position: 2, name: "Life Insurance in Chandler, AZ", item: `${SITE}${PATH}` },
    ],
  };

  return (
    <div className="App grain bg-[#030303]">
      <Seo
        title="Life Insurance in Chandler, AZ | John Campbell Insurance"
        description="Explore life insurance options with John Campbell Insurance, serving individuals and families in Chandler, Arizona with clear, personalized guidance."
        path={PATH}
        type="article"
        jsonLd={[businessEntity, serviceJsonLd, faqJsonLd, breadcrumbJsonLd]}
      />
      <ScheduleProvider>
        <SmoothScroll>
          <Navbar />
          <main>
            <Content />
          </main>
          <Footer />
        </SmoothScroll>
      </ScheduleProvider>
    </div>
  );
}
